/**
 * Prefijos telefónicos, para que el teléfono de la firma tenga un solo formato.
 *
 * ⚠ **La clave es el código ISO y NO el prefijo.** Estados Unidos, Canadá y
 * República Dominicana comparten el +1: con el prefijo como valor, elegir uno
 * cualquiera de los tres seleccionaría al primero de la lista y quien lo eligió
 * vería otro país en el selector. Es el fallo clásico de este control.
 *
 * ⚠ **Treinta países, no los doscientos.** La lista completa convierte el selector
 * en un desplazamiento largo por algo que aquí sólo tiene una respuesta real. Están
 * los de América, los de Europa con los que se opera y los asiáticos de origen de
 * carga; si falta alguno se añade en esta línea y ya.
 */

export type Pais = {
  /** ISO 3166-1 alfa-2. Es la clave, y es única. */
  iso: string
  nombre: string
  /** Con el «+» delante, tal como se imprime. */
  prefijo: string
}

/** El de la agencia. Encabeza la lista porque es la respuesta el 99 % de las veces. */
export const PAIS_POR_DEFECTO = "VE"

/**
 * El teléfono corporativo, **el mismo para todo el mundo**.
 *
 * ⚠ Vive aquí, en el código, y no en una pantalla de configuración: un número de
 * centralita cambia cada varios años, y una pantalla para editarlo es más superficie
 * que mantener que el rato que cuesta cambiar esta línea el día que pase.
 */
export const TELEFONO_CORPORATIVO = "241 823 35 60"

export const PAISES: Pais[] = [
  { iso: "VE", nombre: "Venezuela", prefijo: "+58" },
  // --- América ---
  { iso: "US", nombre: "Estados Unidos", prefijo: "+1" },
  { iso: "CA", nombre: "Canadá", prefijo: "+1" },
  { iso: "MX", nombre: "México", prefijo: "+52" },
  { iso: "CO", nombre: "Colombia", prefijo: "+57" },
  { iso: "BR", nombre: "Brasil", prefijo: "+55" },
  { iso: "AR", nombre: "Argentina", prefijo: "+54" },
  { iso: "CL", nombre: "Chile", prefijo: "+56" },
  { iso: "PE", nombre: "Perú", prefijo: "+51" },
  { iso: "EC", nombre: "Ecuador", prefijo: "+593" },
  { iso: "PA", nombre: "Panamá", prefijo: "+507" },
  { iso: "CR", nombre: "Costa Rica", prefijo: "+506" },
  { iso: "DO", nombre: "República Dominicana", prefijo: "+1" },
  { iso: "UY", nombre: "Uruguay", prefijo: "+598" },
  { iso: "PY", nombre: "Paraguay", prefijo: "+595" },
  { iso: "BO", nombre: "Bolivia", prefijo: "+591" },
  // --- Europa ---
  { iso: "ES", nombre: "España", prefijo: "+34" },
  { iso: "PT", nombre: "Portugal", prefijo: "+351" },
  { iso: "FR", nombre: "Francia", prefijo: "+33" },
  { iso: "DE", nombre: "Alemania", prefijo: "+49" },
  { iso: "IT", nombre: "Italia", prefijo: "+39" },
  { iso: "GB", nombre: "Reino Unido", prefijo: "+44" },
  { iso: "NL", nombre: "Países Bajos", prefijo: "+31" },
  { iso: "BE", nombre: "Bélgica", prefijo: "+32" },
  { iso: "CH", nombre: "Suiza", prefijo: "+41" },
  { iso: "TR", nombre: "Turquía", prefijo: "+90" },
  // --- Asia ---
  { iso: "CN", nombre: "China", prefijo: "+86" },
  { iso: "JP", nombre: "Japón", prefijo: "+81" },
  { iso: "IN", nombre: "India", prefijo: "+91" },
  { iso: "KR", nombre: "Corea del Sur", prefijo: "+82" },
  { iso: "AE", nombre: "Emiratos Árabes Unidos", prefijo: "+971" },
  { iso: "SG", nombre: "Singapur", prefijo: "+65" },
]

/** El prefijo de un ISO, o el de Venezuela si no se conoce. */
export function prefijoDe(iso: string): string {
  return (
    PAISES.find((p) => p.iso === iso)?.prefijo ??
    (PAISES.find((p) => p.iso === PAIS_POR_DEFECTO)?.prefijo as string)
  )
}

/**
 * El teléfono como se imprime: prefijo, espacio, y el número tal cual.
 *
 * ⚠ **Un solo formato para todo el mundo, y por eso se compone aquí.** Dejando que
 * cada quien escriba el número entero salen «0241-8233560», «(241) 823.35.60» y
 * «+58 241 8233560» en tres firmas de la misma agencia. El prefijo lo pone el
 * selector y el resto se normaliza a espacios simples.
 */
export function componerTelefono(iso: string, numero: string): string {
  const limpio = numero.trim().replace(/\s+/g, " ")
  if (limpio === "") return ""
  return `${prefijoDe(iso)} ${limpio}`
}
